import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { setChatToken } from "@/lib/chatSession";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const pinInput = "h-12 text-center text-2xl tracking-[0.6em] font-mono bg-white border-slate-300";
const onlyDigits = (v) => v.replace(/\D/g, "").slice(0, 4);

function ResetSelf({ totp, onDone }) {
  const [pw, setPw] = useState(""); const [code, setCode] = useState(""); const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.post("/chat/pin/reset-self", { password: pw, totp_code: code || null }); toast.success("PIN cleared. Set a new PIN."); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="chat-pin-reset-self">
      <div><Label className="text-xs text-slate-500">Login password</Label><Input data-testid="chat-reset-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="bg-white border-slate-300" /></div>
      {totp && <div><Label className="text-xs text-slate-500">Authenticator code</Label><Input data-testid="chat-reset-totp" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} className="bg-white border-slate-300 font-mono" /></div>}
      <Button data-testid="chat-reset-submit" disabled={!pw || busy} onClick={go} className="w-full bg-teal-600 hover:bg-teal-700 text-white">Reset PIN</Button>
    </div>
  );
}

// Gate: renders children only once a chat-unlock token exists.
export function ChatPinGate({ onUnlocked, compact = false }) {
  const [st, setSt] = useState(null);
  const [pin, setPin] = useState(""); const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(""); const [reset, setReset] = useState(false);
  const load = () => api.get("/chat/status").then((r) => setSt(r.data)).catch((e) => setErr(apiError(e)));
  useEffect(() => { load(); }, []);
  if (!st) return <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  const setMode = !st.pin_set;
  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = setMode ? await api.post("/chat/pin/set", { pin, confirm }) : await api.post("/chat/pin/unlock", { pin });
      setChatToken(data.chat_token); onUnlocked && onUnlocked();
    } catch (e) { setErr(apiError(e)); setPin(""); setConfirm(""); load(); } finally { setBusy(false); }
  };
  const lockedOut = st.locked_until && new Date(st.locked_until) > new Date();
  return (
    <div className={`${compact ? "" : "min-h-[420px]"} flex items-center justify-center p-6`} data-testid="chat-pin-gate">
      <div className="w-full max-w-xs text-center">
        <div className="w-12 h-12 rounded-full bg-teal-50 border border-teal-200 flex items-center justify-center mx-auto"><Lock className="w-5 h-5 text-teal-700" /></div>
        {reset ? (<><h3 className="mt-3 font-bold text-slate-900">Reset chat PIN</h3><div className="mt-4 text-left"><ResetSelf totp={st.totp_enabled} onDone={() => { setReset(false); load(); }} /></div>
          <button className="mt-3 text-xs text-slate-500 underline" onClick={() => setReset(false)}>Back</button></>) : (<>
          <h3 className="mt-3 font-bold text-slate-900" data-testid="chat-pin-title">{setMode ? "Set your chat PIN" : "Enter chat PIN"}</h3>
          {setMode && st.deactivated && <p data-testid="chat-deactivated-msg" className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">Chat deactivated. Set a new PIN to reactivate.</p>}
          <p className="text-xs text-slate-500 mt-1">{setMode ? "Choose a 4-digit PIN. This is separate from your login password." : "Chat locks after 10 minutes of inactivity."}</p>
          <div className="mt-4 space-y-2">
            <Input data-testid="chat-pin-input" type="password" inputMode="numeric" autoFocus placeholder="••••" value={pin} disabled={lockedOut} onChange={(e) => setPin(onlyDigits(e.target.value))} onKeyDown={(e) => e.key === "Enter" && pin.length === 4 && (!setMode || confirm.length === 4) && submit()} className={pinInput} />
            {setMode && <Input data-testid="chat-pin-confirm-input" type="password" inputMode="numeric" placeholder="Confirm" value={confirm} onChange={(e) => setConfirm(onlyDigits(e.target.value))} onKeyDown={(e) => e.key === "Enter" && submit()} className={pinInput} />}
          </div>
          {(err || lockedOut) && <p data-testid="chat-pin-error" className="mt-2 text-xs text-rose-600">{lockedOut ? `Too many wrong PINs. Try again after ${new Date(st.locked_until).toLocaleTimeString()}.` : err}</p>}
          <Button data-testid="chat-pin-submit" disabled={busy || lockedOut || pin.length !== 4 || (setMode && confirm.length !== 4)} onClick={submit} className="mt-4 w-full bg-teal-600 hover:bg-teal-700 text-white">{setMode ? "Set PIN" : "Unlock"}</Button>
          {!setMode && (st.role === "super_admin"
            ? <button data-testid="chat-forgot-pin" className="mt-3 text-xs text-teal-700 underline" onClick={() => setReset(true)}>Forgot PIN / Reset PIN</button>
            : <p className="mt-3 text-[11px] text-slate-400">Forgot your PIN? Ask Super Admin to reset it.</p>)}
        </>)}
      </div>
    </div>
  );
}
